import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import { useCallback, useEffect, useRef } from 'react';

import {
  ANY_AUTO_BRAND_DICTIONARY_SELECT_OPTION,
  ANY_AUTO_MODEL_DICTIONARY_SELECT_OPTION,
} from 'constants/dictionaries';

import type { TCreditPurposeFormFields } from 'src/client/entities/creditForm';
import { useGetAutoModels, useGetAutoManufactureYears } from 'src/client/entities/creditForm';

export const useCarInfoDictionariesLoader = () => {
  const { watch, setValue } = useFormContext<TCreditPurposeFormFields>();
  const autoBrandValue = watch('autoBrand.value');
  const autoModelValue = watch('autoModel.value');
  const autoYearValue = watch('autoYear.value');

  const previousAutoBrandValue = useRef(autoBrandValue);
  const previousAutoModelValue = useRef(autoModelValue);

  const [loadAutoModels] = useGetAutoModels();
  const [loadAutoManufactureYears] = useGetAutoManufactureYears();

  const handleYearsLoad = useCallback(
    async (modelId?: number | string) => {
      const isModelAnyValue = modelId === ANY_AUTO_MODEL_DICTIONARY_SELECT_OPTION.value;
      if (isModelAnyValue || !modelId) {
        return;
      }

      const response = await loadAutoManufactureYears(modelId, true);

      return response.data;
    },
    [loadAutoManufactureYears],
  );

  const handleModelsLoad = useCallback(
    async (brandId?: string) => {
      if (!brandId) {
        return;
      }

      const isBrandAnyValue = brandId === ANY_AUTO_BRAND_DICTIONARY_SELECT_OPTION.value;

      if (isBrandAnyValue) {
        setValue('autoModel', ANY_AUTO_MODEL_DICTIONARY_SELECT_OPTION, { shouldValidate: false });
        return;
      }

      const response = await loadAutoModels(brandId, true);

      return response.data;
    },
    [setValue, loadAutoModels],
  );

  const loadInitialModelsAndYears = useCallback(async () => {
    const models = await handleModelsLoad(String(autoBrandValue || ''));
    if (!models) {
      return;
    }
    const initialModel = (models[autoBrandValue] || []).find((model) => model.label === autoModelValue);
    if (!initialModel) {
      return;
    }
    setValue(
      'autoModel',
      {
        ...initialModel,
        data: { ...(initialModel.data || {}), isPrefilled: true },
      } as NonNullable<TCreditPurposeFormFields['autoModel']>,
      { shouldValidate: false },
    );

    const years = await handleYearsLoad(initialModel.value);
    if (!years) {
      return;
    }

    const initialYear = (years[initialModel.value] || []).find((year) => year.label === autoYearValue);
    if (!initialYear) {
      return;
    }
    setValue(
      'autoYear',
      { ...initialYear, data: { ...(initialYear.data || {}), isPrefilled: true } },
      { shouldValidate: false },
    );
  }, [handleModelsLoad, autoBrandValue, autoModelValue, autoYearValue, setValue, handleYearsLoad]);

  useEffect(() => {
    loadInitialModelsAndYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (previousAutoBrandValue.current === autoBrandValue) {
      return;
    }
    previousAutoBrandValue.current = autoBrandValue;
    handleModelsLoad(String(autoBrandValue || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoBrandValue]);

  useEffect(() => {
    if (previousAutoModelValue.current === autoModelValue) {
      return;
    }
    previousAutoModelValue.current = autoModelValue;
    handleYearsLoad(autoModelValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoModelValue]);
};
